import { Command } from 'commander';
import { WorkflowEngine } from '../core/workflow/workflow-engine';
import { WorkflowTemplateLibrary, TemplateCategory } from '../core/workflow/workflow-templates';
import { ConfigManager } from '../core/config-manager';
import { EnhancedTaskAnalyzer } from '../core/enhanced-task-analyzer';
import { ContextBuilder } from '../core/context/context-builder';
import { ServiceRegistry } from '../core/service-registry';
import { logger } from '../utils/logger';
import chalk from 'chalk';
import inquirer from 'inquirer';
import ora from 'ora';

// 创建任务-工作流联动命令
export function createTaskWorkflowCommand(): Command {
  const taskWorkflow = new Command('tw')
    .description('Task and workflow integration commands')
    .alias('task-workflow');

  const configManager = new ConfigManager();
  const engine = new WorkflowEngine(configManager);
  const templateLibrary = new WorkflowTemplateLibrary();
  const taskAnalyzer = new EnhancedTaskAnalyzer();
  const serviceRegistry = new ServiceRegistry();
  const contextBuilder = new ContextBuilder(serviceRegistry);

  // 启动任务并自动匹配工作流
  taskWorkflow
    .command('start')
    .description('Start a task with automatic workflow matching')
    .option('-d, --description <desc>', 'Task description')
    .option('-t, --type <type>', 'Task type (bug, feature, refactor, etc.)')
    .option('-a, --auto-workflow', 'Automatically select and start workflow')
    .option('-i, --interactive', 'Interactive mode')
    .action(async (options) => {
      const spinner = ora('Analyzing task...').start();
      
      try {
        let taskDescription = options.description;
        let taskType = options.type;

        // 交互式输入
        if (options.interactive && !taskDescription) {
          spinner.stop();
          const answers = await inquirer.prompt([
            {
              type: 'input',
              name: 'description',
              message: 'Task description:',
              validate: (input) => input.trim() ? true : 'Description is required'
            },
            {
              type: 'list',
              name: 'type',
              message: 'Task type:',
              choices: [
                { name: 'Bug Fix', value: 'bug' },
                { name: 'Feature Development', value: 'feature' },
                { name: 'Code Refactoring', value: 'refactor' },
                { name: 'Performance Optimization', value: 'performance' },
                { name: 'Documentation', value: 'docs' },
                { name: 'Testing', value: 'test' },
                { name: 'Security Fix', value: 'security' },
                { name: 'Other', value: 'other' }
              ],
              when: !taskType
            }
          ]);
          
          taskDescription = answers.description || taskDescription;
          taskType = answers.type || taskType;
          spinner.start();
        }

        if (!taskDescription) {
          spinner.fail('Task description is required');
          return;
        }

        spinner.text = 'Analyzing task complexity...';

        // 分析任务
        const analysisResult = await taskAnalyzer.analyzeTask({
          title: taskDescription,
          description: taskDescription,
          type: taskType
        });

        spinner.text = 'Building context...';

        // 构建上下文
        const context = await contextBuilder.build({
          task: {
            description: taskDescription,
            type: analysisResult.type,
            complexity: analysisResult.complexity
          }
        });

        spinner.succeed('Task analysis complete');

        // 显示分析结果
        console.log(chalk.cyan('\n📊 Task Analysis:'));
        console.log(chalk.gray(`  Type: ${chalk.white(analysisResult.type)}`));
        console.log(chalk.gray(`  Complexity: ${getComplexityBadge(analysisResult.complexity.overall)}`));
        console.log(chalk.gray(`  Estimated time: ${chalk.white(analysisResult.estimatedTime)}`));
        console.log(chalk.gray(`  Confidence: ${chalk.white(Math.round(analysisResult.confidence * 100) + '%')}`));

        // 显示推荐的工作流
        const recommendedWorkflows = await recommendWorkflows(analysisResult);
        
        if (recommendedWorkflows.length === 0) {
          console.log(chalk.yellow('\nNo matching workflows found'));
          return;
        }

        console.log(chalk.cyan('\n🔄 Recommended Workflows:'));
        recommendedWorkflows.forEach((rec, index) => {
          console.log(chalk.gray(`  ${index + 1}. ${rec.template.name} (${Math.round(rec.score * 100)}% match)`));
        });

        // 自动选择或让用户选择
        let selectedTemplate;
        
        if (options.autoWorkflow) {
          selectedTemplate = recommendedWorkflows[0].template;
          console.log(chalk.green(`\n✓ Auto-selected: ${selectedTemplate.name}`));
        } else {
          const answer = await inquirer.prompt([
            {
              type: 'list',
              name: 'workflow',
              message: 'Select a workflow:',
              choices: [
                ...recommendedWorkflows.map((rec, index) => ({
                  name: `${rec.template.name} (${Math.round(rec.score * 100)}% match)`,
                  value: rec.template.id
                })),
                { name: 'None (manual task)', value: null }
              ]
            }
          ]);

          if (!answer.workflow) {
            console.log(chalk.yellow('Manual task created without workflow'));
            return;
          }

          selectedTemplate = templateLibrary.get(answer.workflow);
        }

        // 收集工作流参数
        const parameters: Record<string, any> = {};
        if (selectedTemplate.parameters) {
          console.log(chalk.cyan('\n📝 Workflow Parameters:'));
          
          for (const param of selectedTemplate.parameters) {
            // 尝试从任务描述中推断参数
            const inferredValue = inferParameter(param.name, taskDescription, analysisResult);
            
            if (inferredValue !== undefined) {
              parameters[param.name] = inferredValue;
              console.log(chalk.gray(`  ${param.name}: ${chalk.white(inferredValue)} (inferred)`));
            } else if (param.required) {
              const answer = await inquirer.prompt([
                {
                  type: param.type === 'boolean' ? 'confirm' : 
                        param.enum ? 'list' : 'input',
                  name: 'value',
                  message: `${param.name}:`,
                  default: param.default,
                  choices: param.enum
                }
              ]);
              parameters[param.name] = answer.value;
            } else {
              parameters[param.name] = param.default;
            }
          }
        }

        // 创建并启动工作流
        const workflowSpinner = ora('Starting workflow...').start();
        
        try {
          const definition = templateLibrary.createFromTemplate(selectedTemplate.id, parameters);
          engine.registerDefinition(definition);
          
          const instance = await engine.start(definition.id, {
            variables: {
              ...parameters,
              taskDescription,
              taskType: analysisResult.type,
              taskComplexity: analysisResult.complexity,
              context
            }
          });

          workflowSpinner.succeed('Workflow started successfully');
          
          console.log(chalk.green('\n✅ Task and Workflow Created:'));
          console.log(chalk.gray(`  Task: ${taskDescription}`));
          console.log(chalk.gray(`  Workflow: ${definition.name}`));
          console.log(chalk.gray(`  Instance ID: ${instance.id}`));
          console.log(chalk.gray(`  Status: ${instance.status}`));
          
          console.log(chalk.yellow('\nNext steps:'));
          console.log(chalk.gray('  - Use "workflow status" to monitor progress'));
          console.log(chalk.gray('  - Use "workflow task suggest" for next actions'));
          
        } catch (error) {
          workflowSpinner.fail(`Failed to start workflow: ${error.message}`);
        }

      } catch (error) {
        spinner.fail(`Failed to analyze task: ${error.message}`);
        logger.error('Task analysis error:', error);
      }
    });

  // 关联任务和工作流
  taskWorkflow
    .command('link <task-id> <workflow-id>')
    .description('Link a task to a workflow')
    .action(async (taskId, workflowId) => {
      const spinner = ora('Linking task to workflow...').start();
      
      try {
        // TODO: 实现任务和工作流的关联逻辑
        spinner.succeed(`Task ${taskId} linked to workflow ${workflowId}`);
      } catch (error) {
        spinner.fail(`Failed to link: ${error.message}`);
      }
    });

  // 基于当前工作流推荐下一个任务
  taskWorkflow
    .command('suggest')
    .description('Suggest next task based on current workflow')
    .option('-w, --workflow <id>', 'Workflow instance ID')
    .action(async (options) => {
      try {
        let instanceId = options.workflow;
        
        // 如果没有指定，找到最新的运行中的工作流
        if (!instanceId) {
          const runningInstances = engine.getInstances({
            status: 'running' as any,
            limit: 1
          });
          
          if (runningInstances.length === 0) {
            console.log(chalk.yellow('No active workflows found'));
            return;
          }
          
          instanceId = runningInstances[0].id;
        }

        const instance = engine.getInstance(instanceId);
        if (!instance) {
          console.log(chalk.red(`Workflow not found: ${instanceId}`));
          return;
        }

        const definition = engine['definitions'].get(instance.definitionId);
        if (!definition) {
          console.log(chalk.red('Workflow definition not found'));
          return;
        }

        console.log(chalk.cyan('📋 Current Workflow:'));
        console.log(chalk.gray(`  Name: ${definition.name}`));
        console.log(chalk.gray(`  Status: ${instance.status}`));
        console.log(chalk.gray(`  Current Step: ${instance.currentStep || 'N/A'}`));

        // 找到当前步骤和下一个步骤
        const currentStep = definition.steps.find(s => s.id === instance.currentStep);
        const nextSteps = definition.steps.filter(step => 
          step.status === 'pending' &&
          step.dependencies.every(dep => {
            const depStep = definition.steps.find(s => s.id === dep);
            return depStep && (depStep.status === 'completed' || depStep.status === 'skipped');
          })
        );

        if (currentStep) {
          console.log(chalk.cyan('\n🔄 Current Task:'));
          console.log(chalk.white(`  ${currentStep.name}`));
          
          if (currentStep.description) {
            console.log(chalk.gray(`  ${currentStep.description}`));
          }
          
          console.log(chalk.yellow('\n  Suggested actions:'));
          currentStep.actions.forEach(action => {
            console.log(chalk.gray(`    - ${action.name}`));
          });
        }

        if (nextSteps.length > 0) {
          console.log(chalk.cyan('\n⏭️  Next Tasks:'));
          nextSteps.forEach(step => {
            console.log(chalk.white(`  - ${step.name}`));
            if (step.description) {
              console.log(chalk.gray(`    ${step.description}`));
            }
          });
        } else {
          console.log(chalk.green('\n✓ All tasks completed or no next tasks available'));
        }

      } catch (error) {
        console.log(chalk.red(`Failed to get suggestions: ${error.message}`));
        logger.error('Task suggestion error:', error);
      }
    });

  // 批量任务处理
  taskWorkflow
    .command('batch')
    .description('Process multiple tasks in batch')
    .option('-f, --file <path>', 'File containing task list (one per line)')
    .option('-t, --template <id>', 'Apply same workflow template to all tasks')
    .action(async (options) => {
      if (!options.file) {
        console.log(chalk.yellow('Please specify a file with --file'));
        return;
      }

      console.log(chalk.cyan('Batch task processing coming soon...'));
      // TODO: 实现批量任务处理
    });

  return taskWorkflow;
}

// 推荐工作流
async function recommendWorkflows(analysisResult: any): Promise<Array<{
  template: any;
  score: number;
}>> {
  const templateLibrary = new WorkflowTemplateLibrary();
  const allTemplates = templateLibrary.getAll();
  
  const recommendations = allTemplates.map(template => {
    let score = 0;
    
    // 基于任务类型匹配
    const typeScores: Record<string, Record<string, number>> = {
      bug: {
        [TemplateCategory.BUGFIX]: 1.0,
        [TemplateCategory.QUALITY]: 0.5,
        [TemplateCategory.MAINTENANCE]: 0.3
      },
      feature: {
        [TemplateCategory.DEVELOPMENT]: 1.0,
        [TemplateCategory.RELEASE]: 0.3
      },
      refactor: {
        [TemplateCategory.MAINTENANCE]: 1.0,
        [TemplateCategory.QUALITY]: 0.7,
        [TemplateCategory.DEVELOPMENT]: 0.5
      },
      performance: {
        [TemplateCategory.MAINTENANCE]: 0.8,
        [TemplateCategory.QUALITY]: 0.6
      },
      security: {
        [TemplateCategory.BUGFIX]: 0.8,
        [TemplateCategory.QUALITY]: 0.9
      }
    };

    const taskType = analysisResult.type;
    if (typeScores[taskType] && typeScores[taskType][template.category]) {
      score += typeScores[taskType][template.category] * 0.5;
    }

    // 基于复杂度匹配
    if (template.id.includes('hotfix') && analysisResult.complexity.overall < 3) {
      score += 0.3;
    } else if (template.id.includes('standard') && analysisResult.complexity.overall >= 3 && analysisResult.complexity.overall <= 7) {
      score += 0.3;
    } else if (template.id.includes('feature') && analysisResult.complexity.overall > 5) {
      score += 0.3;
    }

    // 基于关键词匹配
    const keywords = analysisResult.keywords || [];
    const templateKeywords = {
      'bugfix-standard': ['bug', 'fix', 'issue', 'error', 'problem'],
      'feature-development': ['feature', 'add', 'implement', 'create', 'new'],
      'hotfix-emergency': ['urgent', 'critical', 'emergency', 'hotfix', 'production'],
      'code-review': ['review', 'check', 'audit', 'quality'],
      'release-preparation': ['release', 'deploy', 'publish', 'version'],
      'performance-optimization': ['performance', 'optimize', 'speed', 'slow', 'bottleneck']
    };

    if (templateKeywords[template.id]) {
      const matchCount = keywords.filter((kw: string) => 
        templateKeywords[template.id].some(tk => 
          kw.toLowerCase().includes(tk) || tk.includes(kw.toLowerCase())
        )
      ).length;
      
      score += Math.min(matchCount * 0.1, 0.2);
    }

    return { template, score };
  });

  // 过滤并排序
  return recommendations
    .filter(rec => rec.score > 0.2)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
}

// 推断参数值
function inferParameter(paramName: string, description: string, analysisResult: any): any {
  const lowerDesc = description.toLowerCase();
  
  // 推断 Issue ID
  if (paramName === 'issueId' || paramName === 'incidentId') {
    const issueMatch = description.match(/#(\d+)|issue[- ]?(\d+)|bug[- ]?(\d+)/i);
    if (issueMatch) {
      return issueMatch[1] || issueMatch[2] || issueMatch[3];
    }
  }

  // 推断严重性
  if (paramName === 'severity') {
    if (lowerDesc.includes('critical') || lowerDesc.includes('urgent')) return 'critical';
    if (lowerDesc.includes('high')) return 'high';
    if (lowerDesc.includes('low')) return 'low';
    return 'medium';
  }

  // 推断版本
  if (paramName === 'version') {
    const versionMatch = description.match(/v?(\d+\.\d+\.\d+)/);
    if (versionMatch) {
      return versionMatch[1];
    }
  }

  // 推断服务名称
  if (paramName === 'affectedService' || paramName === 'targetArea') {
    // 从关键词中提取可能的服务名
    const services = ['api', 'frontend', 'backend', 'database', 'auth', 'payment'];
    for (const service of services) {
      if (lowerDesc.includes(service)) {
        return service;
      }
    }
  }

  return undefined;
}

// 获取复杂度徽章
function getComplexityBadge(complexity: number): string {
  if (complexity <= 3) return chalk.green('Low');
  if (complexity <= 7) return chalk.yellow('Medium');
  return chalk.red('High');
}